import { Box, IconButton, Sheet, Stack } from "@mui/joy";
import EditIcon from "@mui/icons-material/Edit";
import { theme } from "styles/theme";
import { TaskEditorBlock } from "pages/task-editor/utils/taskEditorUtils";
import PreviewDescription from "./PreviewDescription";
import PreviewAction from "./PreviewAction";
import { PreviewSessionApi } from "./usePreviewSession";

interface PreviewBlockProps {
  block: TaskEditorBlock;
  blockSequence: number; // 1-based (raw 위치 — 스타일/세션 키용)
  // 사람이 읽는 블록 라벨 ("블록 3" / "블록 3 · 옵션 1 · 2") — aria 등 표시 전용.
  displayLabel: string;
  mode: "static" | "interactive";
  session: PreviewSessionApi;
  viewerMode: "patient" | "coach";
  totalBlocks: number;
  // 이 블록을 편집 탭의 해당 블록으로 이동시키는 콜백 (정적/체험 모드 공통).
  // 제공되면 블록 hover/포커스 시 전용 편집(✎) 버튼이 노출되고, 그 버튼을 눌렀을
  // 때만 이동한다(블록 본문 클릭은 트리거 아님 — 텍스트 선택·체험 인터랙션 그대로).
  onEdit?: () => void;
}

// 기본 (body) 블록은 흰색 sheet + 둥근 모서리. header 블록은 v1 의 Header1 처럼
// transparent 배경 + 좁은 padding 으로 렌더하고, 두 번째 이후 등장하면 위쪽
// 여백을 더해 본문과의 간격을 살린다.
function PreviewBlock({
  block,
  blockSequence,
  displayLabel,
  mode,
  session,
  viewerMode,
  totalBlocks,
  onEdit,
}: PreviewBlockProps) {
  const isHeader = block.type === "header";
  const showEditButton = Boolean(onEdit);

  const baseSx = isHeader
    ? {
        backgroundColor: "transparent",
        py: "12px",
        pl: "10px",
        ...(blockSequence > 1 && { pt: "24px" }),
      }
    : {
        backgroundColor: "white",
        borderRadius: "12px",
        px: "20px",
        py: "16px",
      };

  return (
    <Sheet
      sx={{
        ...baseSx,
        // 전용 편집 버튼을 코너에 앵커하고, hover/포커스 시에만 드러낸다. 블록
        // 자체는 클릭 타깃이 아니다.
        ...(showEditButton && {
          position: "relative",
          "&:hover .preview-block-edit-btn": { opacity: 1 },
          "&:focus-within .preview-block-edit-btn": { opacity: 1 },
        }),
      }}
    >
      {showEditButton && (
        <IconButton
          className="preview-block-edit-btn"
          size="sm"
          variant="plain"
          color="neutral"
          aria-label={`${displayLabel} 편집`}
          title="편집 탭에서 이 블록 열기"
          onClick={onEdit}
          onKeyDown={(e) => {
            // 체험 모드의 window Enter(다음 블록 진행) 리스너가 preventDefault 하면
            // 포커스된 이 버튼의 Enter 활성화가 씹힌다 — 전파를 끊어 키보드로도
            // 편집 이동이 동작하게 한다.
            if (e.key === "Enter") e.stopPropagation();
          }}
          sx={{
            position: "absolute",
            top: "6px",
            right: "6px",
            minWidth: "26px",
            minHeight: "26px",
            borderRadius: "6px",
            // 기본 opacity 0 — DOM·탭 순서에는 남겨 키보드로 접근 가능하게 한다.
            opacity: 0,
            transition: "opacity 0.15s ease, color 0.15s ease",
            color: theme.vars.palette.Content.Weak,
            // hover 시 불투명 배경이 본문 텍스트를 가리지 않도록 배경은 투명 유지하고
            // 색 변화로만 hover 피드백을 준다 (Joy plain variant 기본 hover bg 제거).
            "--variant-plainHoverBg": "transparent",
            "--variant-plainActiveBg": "transparent",
            "&:hover": {
              backgroundColor: "transparent",
              color: theme.vars.palette.Content.Highlight,
            },
          }}
        >
          <EditIcon sx={{ fontSize: "16px" }} />
        </IconButton>
      )}
      <Stack spacing="12px">
        <PreviewDescription
          description={block.description}
          blockType={block.type}
        />
        {block.action && (
          <Box sx={{ mt: "8px" }}>
            <PreviewAction
              action={block.action}
              blockIndex={blockSequence - 1}
              blockSequence={blockSequence}
              mode={mode}
              session={session}
              viewerMode={viewerMode}
              totalBlocks={totalBlocks}
            />
          </Box>
        )}
      </Stack>
    </Sheet>
  );
}

export default PreviewBlock;
